import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { useToast } from '../ui/use-toast';
import { useUserSettings } from '@/OS/hooks/useUserSettings';
import { Key } from 'lucide-react';

type Scheme = 'none' | 'rsa-oaep' | 'aes-256-gcm' | 'hybrid';

const defaultSettings = {
  scheme: 'hybrid' as Scheme,
  requireKeyVerification: true,
  publicKeyJwk: '' as string, // store public key (optional)
};

function toBase64(bytes: ArrayBuffer) {
  const uint8 = new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < uint8.byteLength; i++) binary += String.fromCharCode(uint8[i]);
  return btoa(binary);
}

function fromBase64(b64: string) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

const EncryptionSettings: React.FC = () => {
  const { toast } = useToast();
  const { settings, isLoaded, updateSetting } = useUserSettings('ai-encryption', defaultSettings);
  const [privateKeyJwk, setPrivateKeyJwk] = useState<string>(''); // kept local to this device
  const [testInput, setTestInput] = useState<string>('Hello MapleOS!');
  const [encryptedJson, setEncryptedJson] = useState<string>('');
  const [decryptedOutput, setDecryptedOutput] = useState<string>('');

  // Load any locally stored private key
  useEffect(() => {
    const saved = localStorage.getItem('ai_encryption_privateKeyJwk');
    if (saved) setPrivateKeyJwk(saved);
  }, []);

  const savePrivateKeyLocal = useCallback((jwk: string) => {
    setPrivateKeyJwk(jwk);
    localStorage.setItem('ai_encryption_privateKeyJwk', jwk);
  }, []);

  const generateRsaKeyPair = useCallback(async () => {
    const kp = await crypto.subtle.generateKey(
      { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
      true,
      ['encrypt', 'decrypt']
    );
    const pubJwk = await crypto.subtle.exportKey('jwk', kp.publicKey);
    const privJwk = await crypto.subtle.exportKey('jwk', kp.privateKey);
    updateSetting('publicKeyJwk', JSON.stringify(pubJwk));
    savePrivateKeyLocal(JSON.stringify(privJwk));
    toast({ title: 'Key pair generated', description: 'RSA-OAEP 2048-bit keys saved.' });
  }, [savePrivateKeyLocal, updateSetting, toast]);

  const importPublicKey = useCallback(async (jwkStr: string) => {
    try {
      const jwk = JSON.parse(jwkStr);
      await crypto.subtle.importKey('jwk', jwk, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['encrypt']);
      updateSetting('publicKeyJwk', jwkStr);
      toast({ title: 'Public key imported' });
    } catch {
      toast({ title: 'Invalid public key', variant: 'destructive' });
    }
  }, [updateSetting, toast]);

  const importPrivateKey = useCallback(async (jwkStr: string) => {
    try {
      const jwk = JSON.parse(jwkStr);
      await crypto.subtle.importKey('jwk', jwk, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt']);
      savePrivateKeyLocal(jwkStr);
      toast({ title: 'Private key imported' });
    } catch {
      toast({ title: 'Invalid private key', variant: 'destructive' });
    }
  }, [savePrivateKeyLocal, toast]);

  const encryptTest = useCallback(async () => {
    setDecryptedOutput('');
    const scheme: Scheme = settings.scheme || 'none';
    const encoder = new TextEncoder();
    const plaintext = encoder.encode(testInput);

    if (scheme === 'none') {
      setEncryptedJson(JSON.stringify({ scheme: 'none', data: toBase64(plaintext) }));
      toast({ title: 'No encryption applied' });
      return;
    }

    if (scheme === 'rsa-oaep') {
      if (!settings.publicKeyJwk) {
        toast({ title: 'Missing public key', description: 'Generate or import a public key first.', variant: 'destructive' });
        return;
      }
      const pub = await crypto.subtle.importKey('jwk', JSON.parse(settings.publicKeyJwk), { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['encrypt']);
      const cipher = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, pub, plaintext);
      setEncryptedJson(JSON.stringify({ scheme, cipher: toBase64(cipher) }));
      toast({ title: 'Encrypted with RSA-OAEP' });
      return;
    }

    if (scheme === 'aes-256-gcm' || scheme === 'hybrid') {
      const aesKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext);
      const aesJwk = await crypto.subtle.exportKey('jwk', aesKey);

      if (scheme === 'aes-256-gcm') {
        setEncryptedJson(JSON.stringify({
          scheme,
          iv: toBase64(iv.buffer),
          cipher: toBase64(cipher),
          key: aesJwk, // for demo only; in production you would store safely or derive
        }));
        toast({ title: 'Encrypted with AES-256-GCM' });
        return;
      }

      // hybrid: wrap AES key with RSA
      if (!settings.publicKeyJwk) {
        toast({ title: 'Missing public key', description: 'Generate or import a public key first.', variant: 'destructive' });
        return;
      }
      const pub = await crypto.subtle.importKey('jwk', JSON.parse(settings.publicKeyJwk), { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['encrypt']);
      const wrapped = await crypto.subtle.encrypt(
        { name: 'RSA-OAEP' },
        pub,
        new TextEncoder().encode(JSON.stringify(aesJwk))
      );
      setEncryptedJson(JSON.stringify({
        scheme,
        iv: toBase64(iv.buffer),
        cipher: toBase64(cipher),
        wrappedKey: toBase64(wrapped),
      }));
      toast({ title: 'Encrypted with Hybrid (AES + RSA wrap)' });
      return;
    }
  }, [settings.scheme, settings.publicKeyJwk, testInput, toast]);

  const decryptTest = useCallback(async () => {
    if (!encryptedJson) return;
    const parsed = JSON.parse(encryptedJson);
    const scheme: Scheme = parsed.scheme;

    if (scheme === 'none') {
      const data = fromBase64(parsed.data);
      setDecryptedOutput(new TextDecoder().decode(data));
      return;
    }

    if (scheme === 'rsa-oaep') {
      if (!privateKeyJwk) {
        toast({ title: 'Missing private key', description: 'Import or generate a private key first.', variant: 'destructive' });
        return;
      }
      const priv = await crypto.subtle.importKey('jwk', JSON.parse(privateKeyJwk), { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt']);
      const plain = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, priv, fromBase64(parsed.cipher));
      setDecryptedOutput(new TextDecoder().decode(plain));
      return;
    }

    if (scheme === 'aes-256-gcm') {
      const aesJwk = parsed.key;
      const iv = fromBase64(parsed.iv);
      const key = await crypto.subtle.importKey('jwk', aesJwk, { name: 'AES-GCM' }, true, ['decrypt']);
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, fromBase64(parsed.cipher));
      setDecryptedOutput(new TextDecoder().decode(plain));
      return;
    }

    if (scheme === 'hybrid') {
      if (!privateKeyJwk) {
        toast({ title: 'Missing private key', description: 'Import or generate a private key first.', variant: 'destructive' });
        return;
      }
      const priv = await crypto.subtle.importKey('jwk', JSON.parse(privateKeyJwk), { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt']);
      const wrappedKeyPlain = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, priv, fromBase64(parsed.wrappedKey));
      const aesJwk = JSON.parse(new TextDecoder().decode(wrappedKeyPlain));
      const iv = fromBase64(parsed.iv);
      const key = await crypto.subtle.importKey('jwk', aesJwk, { name: 'AES-GCM' }, true, ['decrypt']);
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, fromBase64(parsed.cipher));
      setDecryptedOutput(new TextDecoder().decode(plain));
      return;
    }
  }, [encryptedJson, privateKeyJwk, toast]);

  const scheme = (settings.scheme || 'hybrid') as Scheme;
  const requireKeyVerification = !!settings.requireKeyVerification;
  const publicKeyJwk = settings.publicKeyJwk || '';

  return (
    <div className="h-full w-full flex flex-col">
      <Card className="border-0 rounded-none">
        <CardHeader className="py-4">
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            <CardTitle className="text-lg">Encryption Settings</CardTitle>
            <Badge variant="secondary" className="ml-2">AI Output</Badge>
          </div>
          <CardDescription className="mt-1">
            Choose an encryption scheme and manage keys. Hybrid uses AES-GCM for content and RSA-OAEP to wrap the key.
          </CardDescription>
        </CardHeader>
      </Card>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-2xl">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Encryption scheme</Label>
                  <Select value={scheme} onValueChange={(v) => updateSetting('scheme', v as Scheme)}>
                    <SelectTrigger><SelectValue placeholder="Select scheme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="rsa-oaep">RSA-OAEP (2048)</SelectItem>
                      <SelectItem value="aes-256-gcm">AES-256-GCM</SelectItem>
                      <SelectItem value="hybrid">Hybrid (AES + RSA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="text-sm font-medium">Require key verification</div>
                    <div className="text-xs text-muted-foreground">Force private-key access to decrypt output.</div>
                  </div>
                  <Switch checked={requireKeyVerification} onCheckedChange={(v) => updateSetting('requireKeyVerification', v)} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Public key (JWK)</Label>
                  <Input value={publicKeyJwk} onChange={(e) => updateSetting('publicKeyJwk', e.target.value)} placeholder='Paste public JWK JSON' />
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => importPublicKey(publicKeyJwk || '')}>Import Public</Button>
                    <Button size="sm" onClick={generateRsaKeyPair}>Generate Key Pair</Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Private key (local JWK)</Label>
                  <Input value={privateKeyJwk} onChange={(e) => setPrivateKeyJwk(e.target.value)} placeholder='Paste private JWK JSON' />
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => importPrivateKey(privateKeyJwk || '')}>Import Private</Button>
                    <Button size="sm" onClick={() => { savePrivateKeyLocal(privateKeyJwk || ''); toast({ title: 'Private key saved locally' }); }}>Save Local</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Test Encryption</CardTitle>
              <CardDescription>Try encrypting and decrypting sample text with the current settings.</CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <Input value={testInput} onChange={(e) => setTestInput(e.target.value)} placeholder="Text to encrypt..." />
              <div className="flex gap-2">
                <Button onClick={encryptTest}>Encrypt</Button>
                <Button variant="secondary" onClick={decryptTest}>Decrypt</Button>
              </div>
              <div className="space-y-2 text-xs">
                <div className="font-medium">Encrypted JSON</div>
                <pre className="bg-muted p-2 rounded overflow-x-auto">{encryptedJson || '(none)'}</pre>
                <div className="font-medium">Decrypted Output</div>
                <pre className="bg-muted p-2 rounded overflow-x-auto">{decryptedOutput || '(none)'}</pre>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground">
              Notes:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Public keys are saved in your user settings; private keys are stored locally on this device.</li>
                <li>Hybrid mode encrypts content with AES-GCM and wraps the AES key with RSA-OAEP.</li>
                <li>Enable “Require key verification” to enforce decryption with your private key for AI output access.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
};

export default EncryptionSettings;